import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Brain, Megaphone, DollarSign, Package, Heart, Landmark,
  Shield, GraduationCap, ArrowRight
} from 'lucide-react';

// The 15 agents that reason with live LLM intelligence
const LLM_AGENTS = new Set([
  'pricing', 'inventory', 'deal', 'cx', 'fulfillment', 'seo', 'distribution',
  'cmo', 'content_writer', 'geo_targeting', 'reddit', 'hackernews',
  'twitter', 'linkedin', 'guru',
]);

// Early stubs still in beta
const BETA_AGENTS = new Set([
  'content_distribution', 'creator_analytics', 'creator_revenue',
  'insight_messenger', 'rfq_agent', 'three_pl_partnership',
]);

// Notes for special agents
const AGENT_NOTES = {
  goal_manager: 'the CEO agent, "Brahma"',
};

const businessFunctions = [
  {
    name: 'Sales & Marketing',
    icon: Megaphone,
    color: '#3EF1C7',
    agents: [
      'cmo', 'content_writer', 'blog_publisher', 'twitter', 'linkedin',
      'reddit', 'hackernews', 'distribution', 'content_distribution',
      'geo_targeting', 'seo', 'audience_growth', 'sponsorship',
      'creator_analytics', 'creator_revenue', 'content_coach', 'insight_messenger',
    ],
  },
  {
    name: 'Pricing & Deals',
    icon: DollarSign,
    color: '#7A5CFF',
    agents: ['pricing', 'deal'],
  },
  {
    name: 'E-commerce & Fulfillment',
    icon: Package,
    color: '#FFD166',
    agents: ['fulfillment', 'inventory', 'rfq_agent', 'three_pl_partnership'],
  },
  {
    name: 'Customer Service',
    icon: Heart,
    color: '#FF6B8A',
    agents: ['cx'],
  },
  {
    name: 'Finance',
    icon: Landmark,
    color: '#3EF1C7',
    agents: ['cfo'],
  },
  {
    name: 'Compliance & IP',
    icon: Shield,
    color: '#7A5CFF',
    agents: [
      'head_corp_compliance', 'business_license_agent', 'foreign_qualification_agent',
      'franchise_tax_agent', 'compliance_calendar_agent', 'compliance_docs_agent',
      'ra_filing_agent', 'ra_inbox_agent', 'ra_parser_agent', 'head_brand_ip',
      'trademark_filing_agent', 'patent_filing_agent', 'patent_search_agent',
      'brand_search_agent', 'ip_monitoring_agent',
    ],
  },
  {
    name: 'Leadership & Learning',
    icon: GraduationCap,
    color: '#FFD166',
    agents: ['goal_manager', 'guru', 'head_creator_success'],
  },
];

function AgentChip({ agent }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0B1120] border border-[#22304A] text-sm">
      <span className="text-[#94A3B8] font-mono text-xs">{agent}</span>
      {AGENT_NOTES[agent] && (
        <span className="text-xs text-[#64748B]">— {AGENT_NOTES[agent]}</span>
      )}
      {LLM_AGENTS.has(agent) && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#7A5CFF]/10 text-[#7A5CFF] border border-[#7A5CFF]/30 font-medium">LLM</span>
      )}
      {BETA_AGENTS.has(agent) && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FFD166]/10 text-[#FFD166] border border-[#FFD166]/30 font-medium">beta</span>
      )}
    </span>
  );
}

export default function DocsAgentRoster() {
  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#7A5CFF]/10 text-[#7A5CFF] border-[#7A5CFF]/30 mb-4">
          AI Agents
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          The Agent Roster
        </h1>
        <p className="text-lg text-[#94A3B8]">
          43 specialist agents are built today, organized in an 84-role org design that grows as
          the platform does. 15 reason with live LLM intelligence; the rest run deterministic
          playbooks. Here's the honest list.
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-[#3EF1C7]">43</div>
            <div className="text-xs text-[#64748B] mt-1">built</div>
          </CardContent>
        </Card>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-[#7A5CFF]">15</div>
            <div className="text-xs text-[#64748B] mt-1">LLM-reasoning</div>
          </CardContent>
        </Card>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-[#FFD166]">7</div>
            <div className="text-xs text-[#64748B] mt-1">business functions</div>
          </CardContent>
        </Card>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-white">84</div>
            <div className="text-xs text-[#64748B] mt-1">role org design</div>
          </CardContent>
        </Card>
      </div>

      {/* One section per business function */}
      {businessFunctions.map((fn) => {
        const Icon = fn.icon;
        return (
          <section key={fn.name} className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${fn.color}1A` }}>
                <Icon className="w-4 h-4" style={{ color: fn.color }} />
              </div>
              <h2 className="text-2xl font-semibold text-white">
                {fn.name}
              </h2>
              <span className="text-sm text-[#64748B]">{fn.agents.length} {fn.agents.length === 1 ? 'agent' : 'agents'}</span>
            </div>
            <Card className="bg-[#0D1628] border-[#22304A]">
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-2">
                  {fn.agents.map((agent) => (
                    <AgentChip key={agent} agent={agent} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        );
      })}

      {/* Footer note */}
      <section>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <Brain className="w-6 h-6 text-[#7A5CFF] flex-shrink-0 mt-0.5" />
              <p className="text-[#94A3B8]">
                The full 84-role org design (supply-chain planning, warehousing, transportation,
                AP/AR and more) activates progressively — see your live org on the Business OS page.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/dashboard/business-os" className="inline-flex items-center gap-1 text-sm text-[#3EF1C7] hover:text-[#3EF1C7]/80 transition-colors">
                See your Business OS <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/docs/agents" className="inline-flex items-center gap-1 text-sm text-[#3EF1C7] hover:text-[#3EF1C7]/80 transition-colors">
                Agents Overview <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
